import { findProblems, type Collection } from "@tanstack/db";

export async function refreshCollections({
  queries,
  refresh,
  signal,
}: {
  readonly queries: ReadonlyArray<
    Pick<Collection, "status" | "reload" | "health"> | undefined
  >;
  readonly refresh: () => Promise<unknown>;
  readonly signal: AbortSignal;
}): Promise<void> {
  const activeQueries = queries.filter((query) => query !== undefined);

  if (activeQueries.length === 0) return;

  const operations = collectionRefreshOperations({
    queries: activeQueries,
    refresh,
  });

  // Projection sources share a Query entry; overlapping reload cleanup can cancel its peer's fetch.
  for (const operation of operations) {
    if (signal.aborted) return;

    const [result] = await Promise.allSettled([operation()]);

    if (signal.aborted) return;

    if (result.status === "fulfilled") continue;

    const reported = activeQueries.some((query) =>
      findProblems(query.health).some((node) => {
        const problem = node.error ?? node.failureReason;
        return (
          problem !== null &&
          (problem.cause === result.reason ||
            problem.errors.some((error) => error.cause === result.reason))
        );
      }),
    );

    if (!reported) throw result.reason;
  }
}

function collectionRefreshOperations({
  queries,
  refresh,
}: {
  readonly queries: readonly Pick<Collection, "status" | "reload" | "health">[];
  readonly refresh: () => Promise<unknown>;
}): (() => Promise<unknown>)[] {
  const failedQueries = queries.filter(
    (query) =>
      query.status === "error" ||
      query.status === "crashed" ||
      query.health.error !== null,
  );
  let operations: (() => Promise<unknown>)[];

  if (failedQueries.length > 0)
    operations = failedQueries.map((query) => () => query.reload());
  else operations = [refresh];

  return operations;
}
